import { trace } from '@wk/wk-gatherer';
import Router from 'next/router';
import { parse as qsParse } from 'query-string';

import { getSpaceT, SELF_BOOKING } from '../spaceT';
import { ErrorData } from '../types';

import { Toast } from '@/components';
import SelfModal from '@/pageComponents/SelfSeries/SelfModal';
import useUser from '@/store/useUser';
import { isAppEnv } from '@/utils/useGetDevice';
import { appJumpPage } from '@/utils/utils';

enum ErrorTypeEnum {
  ALREADY = 'ALREADY',
  // 时间冲突
  CONFLICT = 'CONFLICT',
  // 班级满员
  FORM_CLASS_FULL = 'FORM_CLASS_FULL',
}

const spaceT = getSpaceT(SELF_BOOKING);

// 安全埋点：任何异常都需静默处理，避免影响业务流程
const safeTrace = (eventName: string, params?: unknown) => {
  try {
    trace(eventName, params as Record<string, unknown>);
  } catch (_) {
    // swallow errors
  }
};

// 从当前上下文获取埋点参数
const getContextTrackingParams = () => {
  const isBrowser = typeof window !== 'undefined';
  // 路径判定（健壮性 + 简化逻辑）
  const pathname =
    isBrowser && typeof window.location?.pathname === 'string'
      ? window.location.pathname
      : '';
  const classType = pathname.includes('private') ? '私教课' : '班课';

  // 从 URL 参数获取 subject/triggerPvid（只在浏览器环境解析）
  const subject = (() => {
    if (!isBrowser) {
      return undefined;
    }
    try {
      const parsed: Record<string, unknown> = qsParse(
        window.location.search || '',
      );
      return typeof parsed.subject === 'string' ? parsed.subject : undefined;
    } catch (_) {
      return undefined;
    }
  })();

  const triggerPvid = (() => {
    if (!isBrowser) {
      return undefined;
    }
    try {
      const parsed: Record<string, unknown> = qsParse(
        window.location.search || '',
      );
      return typeof parsed.triggerPvid === 'string'
        ? parsed.triggerPvid
        : undefined;
    } catch (_) {
      return undefined;
    }
  })();

  // 从 useUser store 获取当前学生数据（防御式读取，避免异常影响流程）
  const { curStudent } = useUser.getState();
  const studyStatus = (() => {
    if (!curStudent?.courseUnits) {
      return undefined;
    }
    const status = { chinese: '', math: '', english: '' };
    for (const unit of curStudent.courseUnits as Array<{
      subject: string;
      remaining: number;
    }>) {
      if (unit.subject === 'CHINESE') {
        status.chinese = `${unit.remaining}`;
      } else if (unit.subject === 'MATH') {
        status.math = `${unit.remaining}`;
      } else if (unit.subject === 'ENGLISH') {
        status.english = `${unit.remaining}`;
      }
    }
    return status;
  })();

  const subjectPayStatus = curStudent
    ? {
        chinese: curStudent.chinesePayStatus || '',
        math: curStudent.mathPayStatus || '',
        english: curStudent.englishPayStatus || '',
      }
    : undefined;
  let studentType: string | undefined;
  // 从全局 store 读取 studentType（订阅/常规）
  const currentOrder = useUser.getState().currentSelfBookingOrder;
  if (currentOrder && typeof currentOrder.subscribeStudent === 'boolean') {
    studentType = currentOrder.subscribeStudent ? '订阅' : '常规';
  }

  return {
    classType,
    subject,
    studyStatus,
    subjectPayStatus,
    studentType,
    triggerPvid,
  };
};

export const selfBooking = (data: ErrorData) => {
  const trackingParams = getContextTrackingParams();

  switch (data.errorType) {
    //课节不在可调范围
    case ErrorTypeEnum.ALREADY: {
      SelfModal.confirm({
        title: spaceT('错误弹窗-选课重复-标题'),
        content: spaceT('错误弹窗-选课重复-内容'),
        type: 'info',
        showCancelButton: false,
        confirmText: spaceT('错误弹窗-选课重复-按钮文案'),
        async onConfirm() {
          safeTrace('C_SelectClass_DuplicateCoursesGo', trackingParams);
          if (isAppEnv()) {
            appJumpPage(
              JSON.stringify({
                path: 'class',
                type: 'INTERNAL',
                customData: {},
              }),
            );
          } else {
            if (window.innerWidth < 1024) {
              await Router.replace('/home');
            } else {
              await Router.replace('/home/lesson');
            }
          }
        },
      });
      break;
    }
    //课节不在可调范围
    case ErrorTypeEnum.CONFLICT: {
      SelfModal.confirm({
        title: spaceT('错误弹窗-时间冲突-标题'),
        content: spaceT('错误弹窗-时间冲突-内容'),
        type: 'info',
        showCancelButton: false,
        confirmText: spaceT('错误弹窗-时间冲突-按钮文案'),
        async onConfirm() {
          safeTrace('C_SelectClass_ScheduleConflictBack', trackingParams);
          Router.back();
        },
      });
      break;
    }
    //课节不在可调范围
    case ErrorTypeEnum.FORM_CLASS_FULL: {
      SelfModal.confirm({
        title: spaceT('错误弹窗-班级满员-标题'),
        content: spaceT('错误弹窗-班级满员-内容'),
        type: 'info',
        showCancelButton: false,
        confirmText: spaceT('错误弹窗-班级满员-按钮文案'),
        async onConfirm() {
          safeTrace('C_SelectClass_ClassFullBack', trackingParams);
          Router.back();
        },
      });
      break;
    }

    default:
      Toast.error(data.errorMsg);
      break;
  }
};
